import { format } from 'date-fns'
import { enIE, enGB, enUS } from 'date-fns/locale'
import i18n from '../i18n/config'

/**
 * Get date-fns locale based on current i18n locale
 */
export function getDateLocale() {
  const locale = i18n.language || 'en-IE'
  
  if (locale.startsWith('en-GB')) return enGB
  if (locale.startsWith('en-US')) return enUS
  return enIE // Default to Ireland
}

/**
 * Format date according to locale
 * Ireland/UK: DD/MM/YYYY
 * US: MM/DD/YYYY
 */
export function formatDate(date: string | Date, formatStr: string = 'dd/MM/yyyy'): string {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    const locale = getDateLocale()
    
    // Adjust format based on locale
    if (i18n.language?.startsWith('en-US')) {
      formatStr = formatStr.replace('dd/MM/yyyy', 'MM/dd/yyyy')
    }
    
    return format(dateObj, formatStr, { locale })
  } catch {
    return String(date)
  }
}

/**
 * Format phone number for Ireland
 * Input: 0851234567 or +353851234567
 * Output: 085 123 4567 or +353 85 123 4567
 */
export function formatPhoneNumber(phone: string, countryCode: string = 'IE'): string {
  if (!phone) return ''
  
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '')
  
  if (countryCode === 'IE') {
    // Irish phone number formatting
    if (cleaned.startsWith('+353')) {
      // International format: +353 85 123 4567
      const withoutCode = cleaned.substring(4)
      if (withoutCode.length === 9) {
        return `+353 ${withoutCode.substring(0, 2)} ${withoutCode.substring(2, 5)} ${withoutCode.substring(5)}`
      }
    } else if (cleaned.startsWith('0')) {
      // National format: 085 123 4567
      if (cleaned.length === 10) {
        return `${cleaned.substring(0, 3)} ${cleaned.substring(3, 6)} ${cleaned.substring(6)}`
      }
    }
  }
  
  // Return cleaned if we can't format
  return cleaned
}

/**
 * Validate Irish Eircode format
 * Format: A12 BCDE (2 letters, 2 digits, space, 4 characters)
 */
export function validateEircode(eircode: string): boolean {
  if (!eircode) return true // Optional field
  const eircodeRegex = /^[A-Z]{1,2}\d{1,2}\s?[A-Z0-9]{4}$/i
  return eircodeRegex.test(eircode.replace(/\s/g, ''))
}

/**
 * Format Eircode with space
 */
export function formatEircode(eircode: string): string {
  if (!eircode) return ''
  const cleaned = eircode.replace(/\s/g, '').toUpperCase()
  if (cleaned.length >= 3) {
    return `${cleaned.substring(0, 3)} ${cleaned.substring(3)}`
  }
  return cleaned
}

/**
 * Get address field labels based on locale
 */
export function getAddressLabels() {
  const locale = i18n.language || 'en-IE'
  
  if (locale.startsWith('en-US')) {
    return {
      street: 'Street Address',
      city: 'City',
      state: 'State',
      postalCode: 'ZIP Code',
      country: 'Country',
    }
  }
  
  if (locale.startsWith('en-GB')) {
    return {
      street: 'Street Address',
      city: 'City',
      state: 'County',
      postalCode: 'Postcode',
      country: 'Country',
    }
  }
  
  // Ireland (default)
  return {
    street: 'Street Address',
    city: 'City',
    state: 'County',
    postalCode: 'Eircode',
    country: 'Country',
  }
}

/**
 * Get default country based on locale
 */
export function getDefaultCountry(): string {
  const locale = i18n.language || 'en-IE'
  
  if (locale.startsWith('en-US')) return 'USA'
  if (locale.startsWith('en-GB')) return 'United Kingdom'
  return 'Ireland' // Default
}
